import 'react-phone-number-input/style.css';

import { Input } from 'antd';
import AddressSuggestion, { ISuggestionResult } from 'components/AddressSuggestion';
import { useFormikContext } from 'formik';
import React, { useMemo, useRef, useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import styled from 'styled-components';

import { ICommonInputProps } from './types';

const InputContainerStyled = styled.div<{ $prefix?: string; $affix?: string }>`
  position: relative;
  width: 100%;
  &::before {
    content: '${({ $prefix }) => $prefix || ''}';
    position: absolute;
    z-index: 10;
    left: 10px;
    top: 50%;
    transform: translate(0, -50%);
  }
  &::after {
    content: '${({ $affix }) => $affix || ''}';
    position: absolute;
    transform: translateY(-50%);
    top: 50%;
    margin-left: 5px;
  }
  input {
    text-overflow: ellipsis;
  }
`;

const PhoneInputStyled = styled(PhoneInput)`
  input[type='tel'] {
    padding: 7.9px 11px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    transition: all 0.3s;
    &:hover {
      border-color: #28aaeb;
    }
    &:focus {
      border-color: #28aaeb;
      border-right-width: 1px !important;
      outline: 0;
      box-shadow: 0 0 0 2px rgb(3 143 222 / 20%);
    }
  }
`;

const TextInput: React.FC<ICommonInputProps> = ({
  disabled,
  name,
  onBlur,
  type,
  placeholder,
  defaultValue,
  inputSettings,
  inputClassName,
  onChange,
  value,
  onKeyUp,
  onEnter,
  addressSuggestion = false,
  addressSuggestionSubFields = {},
}) => {
  const inputPaddingsStyle = useMemo(() => {
    const tempStyle: Record<string, any> = {};
    if (inputSettings?.prefix) {
      tempStyle.paddingLeft = 12 + inputSettings.prefix.length * 8;
    }
    return tempStyle;
  }, []);

  const [isSuggestionVisible, setSuggestionVisible] = useState(false);

  const activeCount = useRef(0);
  const { setFieldValue } = useFormikContext() || { setFieldValue: () => {} };

  const clearCoordinateOnInputChange = () => {
    if (addressSuggestion && addressSuggestionSubFields.longtitude && addressSuggestionSubFields.latitude) {
      setFieldValue(addressSuggestionSubFields.longtitude, '');
      setFieldValue(addressSuggestionSubFields.latitude, '');
    }
  };

  const handleChange = (e: any) => {
    const newInputValue = e.target.value;
    onChange?.(newInputValue, name);
    clearCoordinateOnInputChange();
  };

  const onKeyUpBase = (e: any) => {
    if (e.key === 'Enter' || e.keyCode === 13) {
      // Do something
      onEnter?.(e);
    }
    onKeyUp?.(e);
  };

  const hideSuggestion = () => {
    activeCount.current = activeCount.current === 0 ? 0 : activeCount.current - 1;
    if (activeCount.current === 0) {
      setSuggestionVisible(false);
    }
  };

  const showSuggestion = () => {
    activeCount.current += 1;
    setSuggestionVisible(addressSuggestion);
  };

  const handleBlur = (e: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onBlur ? onBlur(e) : 0;
    setTimeout(() => {
      hideSuggestion();
    }, 500);
  };

  const handleFocus = () => {
    showSuggestion();
  };

  const handleSuggestionClick = (result: ISuggestionResult) => {
    if (addressSuggestionSubFields.address1 && result.address1) {
      setFieldValue(addressSuggestionSubFields.address1, result.address1);
    }
    // TODO: maybe set address2 too
    if (addressSuggestionSubFields.city && result.city) {
      setFieldValue(addressSuggestionSubFields.city, result.city);
    }
    if (addressSuggestionSubFields.zip && result.zip) {
      setFieldValue(addressSuggestionSubFields.zip, result.zip);
    }
    if (addressSuggestionSubFields.state && result.state) {
      setFieldValue(addressSuggestionSubFields.state, result.state);
    }
    if (addressSuggestionSubFields.longtitude && result.longtitude) {
      setFieldValue(addressSuggestionSubFields.longtitude, result.longtitude);
    }
    if (addressSuggestionSubFields.latitude && result.latitude) {
      setFieldValue(addressSuggestionSubFields.latitude, result.latitude);
    }
  };

  const inputAutocomplete = addressSuggestion ? 'off' : inputSettings?.autocomplete;

  const formatDefaultValue = defaultValue;

  return (
    <InputContainerStyled
      $prefix={inputSettings?.prefix}
      $affix={inputSettings?.affix}
      className={`text-input ${inputClassName}`}>
      {inputSettings?.isPhoneNumber ? (
        <PhoneInputStyled
          style={{
            ...inputPaddingsStyle,
          }}
          defaultCountry='US'
          autoComplete={inputAutocomplete}
          name={name}
          value={value}
          // defaultValue={formatDefaultValue}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e: any) => handleChange({ target: { value: e } })}
          onKeyUp={onKeyUpBase}
          onBlur={handleBlur}
          onFocus={handleFocus}
        />
      ) : (
        <Input
          style={{
            ...inputPaddingsStyle,
          }}
          autoComplete={inputAutocomplete}
          name={name}
          value={value}
          defaultValue={formatDefaultValue}
          placeholder={placeholder}
          type={type}
          disabled={disabled}
          onChange={handleChange}
          onKeyUp={onKeyUpBase}
          onBlur={handleBlur}
          onFocus={handleFocus}
        />
      )}
      {addressSuggestion && (
        <AddressSuggestion searchText={value} onChange={handleSuggestionClick} visible={isSuggestionVisible} />
      )}
    </InputContainerStyled>
  );
};

export default React.memo(TextInput);
